import {
  CellClassParams,
  CellClickedEvent,
  CellKeyDownEvent,
  ColDef,
  ColumnState,
  GridApi,
  ICellRendererParams,
} from 'ag-grid-community';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';
import {IDisableStockItemRule} from '../../settings-new/models';
import {
  AOStockInfo,
  FormattedStockInfoStrings,
} from '../models/old-models/auto-order-types';
import {
  AutoOrderOpenOrderRefinementFiltersDefaults,
  IAutoOrderFilterOrderSettings,
  IAutoOrderFiltersAndTools,
  IAutoOrderOpenOrderRefinementFilters,
  IAutoOrderOrder,
  IAutoOrderOrderItem,
} from '../models';
import {calculateQuantityDifference, roundSuggestedOrderQty} from './calculations';
import {StockFunctions} from '../../../shared-utilities/functions-old/stock-functions';
import {exportDataToExcel} from '../../../shared/shared-utils/export/export-utils';
import {ISupplier} from '../../../shared/shared-models/stock/suppliers';
import {TLineColour} from '../../settings-new/models';
import {decodeOnHoldCode} from '../../../shared/shared-module-ui/utils/on-hold-code/on-hold-code.utils';

//#region CELL RENDERERS

export function codeCellRenderer(
  params: ICellRendererParams,
  autoOrderItemsObj: Record<string, IAutoOrderOrderItem>,
  stockInfo: Record<string, AOStockInfo>,
): string {
  const code = params.value;
  return `<span class="code-text">${code}</span>`;
}

export function qtyDiffCellRenderer(params: ICellRendererParams): string {
  const value = params.value;
  if (value === undefined || value === null || isNaN(value)) {
    return 'NaN';
  }
  return String(value);
}

export function orderQtyCellRenderer(
  params: ICellRendererParams,
): string {
  const value = params.value;
  return `<span class="qty-value">${value ?? ''}</span>`;
}

export function onHoldCellRenderer(
  params: ICellRendererParams,
  isItemOnHold: boolean,
  tooltip: string,
): string {
  const code = params?.data?.code;
  if (!code) {
    return;
  }

  if (isItemOnHold) {
    return `<button
        class="on-hold-toggle play-button"
        data-action="toggle-hold"
        data-code="${code}"
        data-set-hold="false"
        title="${tooltip.replace(/\n/g, '&#10;')}"
        style="background: transparent; cursor: pointer; border: none; padding: 0; display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: var(--global-colour-warning) ">
        <div style="border: 2px solid var(--gallix-color-warning); border-radius: 50%; width: 20px; height: 20px; display: flex; justify-content: center;align-items: center; margin-right: 5px;">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M8,5.14V19.14L19,12.14L8,5.14Z" fill="var(--global-colour-warning)"/>
        </svg>
        </div>
    
      </button>`;
  } else {
    return `<button
        class="on-hold-toggle pause-button"
        data-action="toggle-hold"
        data-code="${code}"
        data-set-hold="true"
        title="${tooltip.replace(/\n/g, '&#10;')}"
        style="color: var(--global-colour-success); background: transparent; cursor: pointer; border: none; padding: 0; display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">
         <div style="border: 2px solid var(--global-colour-success); border-radius: 50%; width: 20px; height: 20px; display: flex; justify-content: center;align-items: center; margin-right: 5px;">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" >
  <path d="M14,19H18V5H14M6,19H10V5H6V19Z" fill="var(--global-colour-success)"/>
</svg>
        </div>
      </button>`;
  }
}

//#endregion

//#region CELL CLASS CALCULATORS

export function getPriceCellClass(
  params: CellClassParams,
  priceEdits: Record<string, number>,
  selectedOrder: IAutoOrderOrder,
  orderQuantity?: Record<string, number>,
  stockInfo?: Record<string, AOStockInfo>,
): string {
  const code = params?.data?.code;

  if (!code) {
    return '';
  }

  const classes: string[] = ['edit-price'];

  if (Object.prototype.hasOwnProperty.call(priceEdits, code)) {
    const value = +priceEdits[code];

    if (!value || value < 0) {
      classes.push('invalid');
    }

    if (selectedOrder?.preparedInfo?.preparedItems?.[code]?.price !== value) {
      classes.push('edited');
    } else {
      classes.push('saved');
    }
  }

  if (orderQuantity && stockInfo && orderQuantity[code]) {
    const price = priceEdits[code] ?? stockInfo[code]?.latestCost ?? 0;

    if (price <= 0) {
      classes.push('price-no-qty');
    }
  }

  return classes.join(' ');
}

export function getOrderQtyCellClass(
  params: CellClassParams,
  orderQuantity: Record<string, number>,
  invalidItems: Record<string, {
    price?: { reason: string; allowPrepare: boolean; valid: boolean };
    quantity?: { reason: string; allowPrepare: boolean; valid: boolean };
  }>,
  selectedOrder: IAutoOrderOrder,
  stockInfo: Record<string, AOStockInfo>,
  priceEdits: Record<string, number>,
): string {
  const code = params?.data?.code;
  if (!code) {
    return;
  }
  let cssClasses = '';

  if (isItemOnHoldForPurchaseOrders(code, stockInfo)) {
    cssClasses += ' on-hold';
  }

  if (invalidItems[code]?.quantity?.valid === false) {
    cssClasses += ' invalid';
  }

  if (orderQuantity[code]) {
    if (selectedOrder?.preparedInfo?.preparedItems?.[code]?.qty !== +orderQuantity[code]) {
      cssClasses += ' edited';
    } else {
      cssClasses += ' saved';
    }

    if (stockInfo[code]?.packSize) {
      if (orderQuantity[code] % stockInfo[code].packSize !== 0) {
        cssClasses += ' not-multiple-pack';
      }
    }

    const price = priceEdits[code] ?? stockInfo[code]?.latestCost ?? 0;
    if (price <= 0) {
      cssClasses += ' price-no-qty';
    }
  } else if (priceEdits.hasOwnProperty(code) && !orderQuantity.hasOwnProperty(code)) {
    cssClasses += ' price-no-qty';
  }

  return cssClasses.trim();
}

//#endregion

//#region DATA FORMATTERS

export function formatNumericValue(value: unknown): string {
  if (!value && value !== 0) return '0';
  const numValue = Number(value);
  const wholeValue = Number.isInteger(numValue) ? Math.floor(numValue) : numValue;
  return String(wholeValue);
}

//#endregion

//#region BUSINESS LOGIC FUNCTIONS

/**
 * Determines if a stock item's supplier matches the order's supplier
 */
export function isItemFromOrderSupplier(
  itemSupplierCode: string,
  orderSupplierCode: string,
): boolean {
  if (!itemSupplierCode || !orderSupplierCode) {
    return false;
  }
  return itemSupplierCode.trim().toLowerCase() === orderSupplierCode.trim().toLowerCase();
}

/**
 * Determines if a stock item is from a different supplier than the order's supplier
 */
export function isItemFromOtherSupplier(
  itemSupplierCode: string,
  orderSupplierCode: string,
): boolean {
  return !isItemFromOrderSupplier(itemSupplierCode, orderSupplierCode);
}

/**
 * Items added manually via Add Item — always visible regardless of supplier toggles.
 */
export function isManuallyAddedOrderItem(orderItem: IAutoOrderOrderItem | undefined): boolean {
  return orderItem?.new === 'ADDED';
}

export function calcQtyDiff(
  toggles: IAutoOrderFiltersAndTools,
  selectedOrder: IAutoOrderOrder,
  autoOrderItemsObj: Record<string, IAutoOrderOrderItem>,
  stockInfo: Record<string, AOStockInfo>,
  displayData: Record<string, FormattedStockInfoStrings>,
  orderSupplierCode: string,
): Record<string, FormattedStockInfoStrings> {
  const subPOMultiplier: 1 | 0 = toggles.isSubtractPOOn ? 1 : 0;
  const displayDataObj = {...displayData};

  const qdCalc = (code: string): number | null => {
    if (autoOrderItemsObj[code]?.new === true || autoOrderItemsObj[code]?.new === 'ADDED') {
      return null;
    }

    const qtyPO = subPOMultiplier * (stockInfo[code]?.purchaseOrder ?? 0);
    const isAddZeroDays: number = toggles.isIncludeDaysZeroOn ? autoOrderItemsObj[code]?.zeroDays : 0;
    const isAddDaysNegative: number = toggles.isIncludeNegativeDaysOn ? autoOrderItemsObj[code]?.negativeDays : 0;

    const qtyDifference = calculateQuantityDifference(
      autoOrderItemsObj[code].netSold,
      selectedOrder.orderSettings.monthsHistory,
      selectedOrder.orderSettings.stockDays,
      selectedOrder.orderSettings.growth,
      isAddZeroDays,
      isAddDaysNegative,
    );
    const qtyDiffMinusOnHand = qtyDifference - +stockInfo[code]?.onHand - qtyPO;

    if (toggles.isMeetOrderLevelOn) {
      const itemOrdLvl = +(stockInfo[code]?.ordLvl ?? 0);
      let suggestedQty = qtyDiffMinusOnHand;
      if (itemOrdLvl > 0 && qtyDiffMinusOnHand < itemOrdLvl) {
        suggestedQty = itemOrdLvl - +(stockInfo[code]?.onHand ?? 0) - qtyPO;
      }
      return Math.max(0, suggestedQty);
    }

    return qtyDiffMinusOnHand;
  };

  const setQtyDiffFromCalc = (code: string): void => {
    if (!displayDataObj[code]) {
      displayDataObj[code] = {} as FormattedStockInfoStrings;
    }

    const qtyDiff = qdCalc(code);
    if (qtyDiff !== null) {
      const numQtyDiff = Number(qtyDiff);
      if (!isNaN(numQtyDiff)) {
        displayDataObj[code].qtyDiff = String(roundSuggestedOrderQty(numQtyDiff));
      } else {
        displayDataObj[code].qtyDiff = '0';
      }
    } else {
      displayDataObj[code].qtyDiff = 'NaN';
    }
  };

  const setQtyDiffFromExisting = (code: string): void => {
    if (!displayDataObj[code]) {
      displayDataObj[code] = {} as FormattedStockInfoStrings;
    }

    const orderItem = autoOrderItemsObj[code];
    if (orderItem?.new === true || orderItem?.new === 'ADDED') {
      displayDataObj[code].qtyDiff = 'NaN';
      return;
    }

    const rawQtyDiff = orderItem?.qtyDiff ?? displayDataObj[code]?.qtyDiff;
    const numQtyDiff = Number(rawQtyDiff);
    if (isNaN(numQtyDiff)) {
      return;
    }

    displayDataObj[code].qtyDiff = String(roundSuggestedOrderQty(numQtyDiff));
  };

  Object.keys(autoOrderItemsObj).forEach((code: string) => {
    const itemSupplierCode = stockInfo[code]?.regularSuppCode ?? '';
    const isOrderSupplier = isItemFromOrderSupplier(itemSupplierCode, orderSupplierCode);
    const isOtherSupplier = isItemFromOtherSupplier(itemSupplierCode, orderSupplierCode);

    const isVisible =
      isManuallyAddedOrderItem(autoOrderItemsObj[code]) ||
      (toggles.isViewSupplierItemsOn && isOrderSupplier) ||
      (toggles.isViewNonSupplierItemsOn && isOtherSupplier);

    if (!isVisible) {
      return;
    }

    if (toggles.isViewSupplierItemsOn && autoOrderItemsObj[code]?.mainSupplier === 1) {
      setQtyDiffFromCalc(code);
    } else {
      setQtyDiffFromExisting(code);
    }
  });

  return displayDataObj;
}

export function filterAndUpdateRowData(
  toggles: IAutoOrderFiltersAndTools,
  filters: IAutoOrderFilterOrderSettings,
  displayData: Record<string, FormattedStockInfoStrings>,
  autoOrderItemsObj: Record<string, IAutoOrderOrderItem>,
  stockInfo: Record<string, AOStockInfo>,
  orderQuantity: Record<string, number>,
  searchString: string,
  stockDisableRules: IDisableStockItemRule,
  lineColours: TLineColour,
  orderSupplierCode: string,
  refinements: IAutoOrderOpenOrderRefinementFilters = AutoOrderOpenOrderRefinementFiltersDefaults,
): Record<string, unknown>[] {
  let codes = Object.keys(stockInfo);

  if (toggles.isViewSupplierItemsOn && !toggles.isViewNonSupplierItemsOn) {
    codes = codes.filter((code: string) => {
      if (isManuallyAddedOrderItem(autoOrderItemsObj[code])) {
        return true;
      }
      const itemSupplierCode = stockInfo[code]?.regularSuppCode;
      return isItemFromOrderSupplier(itemSupplierCode, orderSupplierCode);
    });
  } else if (!toggles.isViewSupplierItemsOn && toggles.isViewNonSupplierItemsOn) {
    codes = codes.filter((code: string) => {
      if (isManuallyAddedOrderItem(autoOrderItemsObj[code])) {
        return true;
      }
      const itemSupplierCode = stockInfo[code]?.regularSuppCode;
      return isItemFromOtherSupplier(itemSupplierCode, orderSupplierCode);
    });
  } else if (!toggles.isViewSupplierItemsOn && !toggles.isViewNonSupplierItemsOn) {
    codes = codes.filter((code: string) => isManuallyAddedOrderItem(autoOrderItemsObj[code]));
  }

  if (filters.includedLineColors && filters.includedLineColors.length > 0) {
    codes = codes.filter((code: string): boolean => {
      return filters.includedLineColors.includes(String(stockInfo[code].lineColourCode));
    });
  } else if (filters.selectedLineColors && filters.selectedLineColors.length > 0) {
    codes = codes.filter((code: string): boolean => {
      return !filters.selectedLineColors.includes(String(stockInfo[code].lineColourCode));
    });
  }

  if (filters.suggestionPositive) {
    codes = codes.filter((code: string): boolean => {
      return +displayData[code]?.qtyDiff > 0;
    });
  }

  if (filters.quantityPositive) {
    codes = codes.filter((code: string): boolean => {
      return orderQuantity[code] && orderQuantity[code] > 0;
    });
  }

  if (searchString?.trim()) {
    codes = codes.filter((code: string): boolean => {
      const item = stockInfo[code];
      const searchLower = searchString.toLowerCase();
      return (
        code.toLowerCase().includes(searchLower) ||
        item.desc?.toLowerCase().includes(searchLower) ||
        item.suppCode?.toLowerCase().includes(searchLower)
      );
    });
  }

  if (refinements.selectedDepartments.length > 0) {
    codes = codes.filter((code: string): boolean => {
      const item = stockInfo[code] as StockItem;
      return refinements.selectedDepartments.includes(String(item.dep));
    });
  }

  if (refinements.selectedSubDepartments.length > 0) {
    codes = codes.filter((code: string): boolean => {
      const item = stockInfo[code] as StockItem;
      return refinements.selectedSubDepartments.includes(String(item.subDep));
    });
  }

  const enabledCodes: string[] = [];
  const disabledCodes: string[] = [];

  codes.forEach((code: string) => {
    const stockItem = stockInfo?.[code] as StockItem | undefined;
    if (
      stockItem &&
      (StockFunctions.isDisabled(stockItem, stockDisableRules) || stockItem?._tags?.includes('disabled'))
    ) {
      disabledCodes.push(code);
    } else {
      enabledCodes.push(code);
    }
  });

  const sortedCodes = [...enabledCodes, ...disabledCodes];

  return sortedCodes.map((code: string) => {
    const stockItem = stockInfo[code];
    const displayItem = displayData[code];
    const orderItem = autoOrderItemsObj[code];

    let lineColourValue: string = '';
    let lineColourDescription: string = '';
    if (lineColours && stockItem.lineColourCode && stockItem.lineColourCode > 0) {
      const colour = lineColours[stockItem.lineColourCode];
      if (colour) {
        lineColourValue = colour.value;
        lineColourDescription = colour.description;
      }
    }

    return {
      code: code,
      desc: stockItem.desc,
      suppCode: stockItem.suppCode,
      dlvColl: stockItem.dlvColl,
      regularSuppCode: stockItem.regularSuppCode,
      onHand: stockItem.onHand,
      packSize: stockItem.packSize,
      purchaseOrder: stockItem.purchaseOrder,
      unitsYear: stockItem.unitsYear,
      maxSold: orderItem.maxSold,
      netSold: orderItem.netSold,
      maxReturned: orderItem.maxReturned,
      ordLvl: stockItem.ordLvl,
      orderQuantity: stockItem.orderQuantity,
      gp1: stockItem.gp1,
      sellPriIncl1: stockItem.sellPriIncl1,
      created: stockItem.created,
      lastStockTake: stockItem.lastStockTake,
      modified: stockItem.modified,
      lastSold: stockItem.lastSold,
      latestCost: stockItem.latestCost,
      onHoldCode: stockItem.onHoldCode,
      lineColourCode: stockItem.lineColourCode,
      mainSupplier: orderItem.mainSupplier,
      outOfStock: orderItem.outOfStock,
      negativeDays: orderItem.negativeDays,
      zeroDays: orderItem.zeroDays,
      qtyDiff: displayItem?.qtyDiff ? parseFloat(displayItem.qtyDiff) : null,
      lineColourValue: lineColourValue,
      lineColourDescription: lineColourDescription,
      poOnHold: stockItem.poOnHold,
      flagged: stockItem.flagged,
      new: orderItem.new,
      _tags: stockItem._tags,
    };
  });
}

//#endregion

//#region ON HOLD HELPERS

export function isItemOnHoldForPurchaseOrders(code: string, stockInfo: Record<string, AOStockInfo>): boolean {
  const item = stockInfo[code];
  if (!item) return false;
  const decoded: string[] = decodeOnHoldCode(item.onHoldCode, {returnCodes: true});
  return decoded.includes('16');
}

export function isItemOnHoldForPurchaseOrdersTooltip(
  code: string,
  stockInfo: Record<string, AOStockInfo>,
  add: boolean = false,
): string {
  const item = stockInfo[code];
  if (!item) return '';

  const decoded: string[] = decodeOnHoldCode(item.onHoldCode, {returnCodes: false})
    .sort((itemA: string, itemB: string) => itemA > itemB ? 1 : -1);

  let tooltip = '';
  if (decoded?.length > 0) {
    tooltip = `The following are 'On Hold' for this item (${code}):\n\n`;
    decoded.forEach((onHoldItem: string) => {
      if (onHoldItem === 'Purchase Orders') {
        tooltip += `->\t${onHoldItem}\n`;
      } else {
        tooltip += `\t${onHoldItem}\n`;
      }
    });
  }
  tooltip += `\nClick to ${add ? 'add' : 'remove'} hold 'Purchase Orders' for this item (${code})`;
  return tooltip;
}

//#endregion

//#region COLUMN STATE MANAGEMENT

/**
 * Sorts column definitions to match the canonical open-order grid column order.
 */
export function sortColDefsByDefaultOrder(colDefs: ColDef[], order: readonly string[]): ColDef[] {
  const orderIndex = new Map<string, number>(order.map((field: string, index: number): [string, number] => [field, index]));
  const getField = (col: ColDef): string | undefined => col.field ?? col.colId;

  return colDefs.slice().sort((a: ColDef, b: ColDef): number => {
    const aField = getField(a);
    const bField = getField(b);
    const ai = aField != null && orderIndex.has(aField) ? orderIndex.get(aField) : Number.MAX_SAFE_INTEGER;
    const bi = bField != null && orderIndex.has(bField) ? orderIndex.get(bField) : Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });
}

export function loadColumnState(
  columnOrderKey: string,
  colDefs: ColDef[],
): ColumnState[] | null {
  try {
    const raw = localStorage.getItem(columnOrderKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;

    const critical = new Set(['code', 'desc', 'icons', 'orderQty', 'onHold']);
    const getColId = (d: ColDef): string | undefined => (d.colId ?? d.field);

    const defOrder = new Map<string, number>();
    const validIds = new Set<string>();
    colDefs.forEach((d, i) => {
      const id = getColId(d);
      if (id) {
        validIds.add(id);
        defOrder.set(id, i);
      }
    });

    const enforcePin = (s: ColumnState): ColumnState => {
      const id = s.colId;
      if (!id) return s;
      if (id === 'code' || id === 'desc' || id === 'icons') return {...s, pinned: 'left'};
      if (id === 'orderQty' || id === 'onHold' || id === 'priceEdits') return {...s, pinned: 'right'};
      return s;
    };

    const savedIds = (typeof parsed[0] === 'string' ? parsed as string[] : []).filter(id => validIds.has(id));
    const orderIndex = new Map<string, number>(savedIds.map((id, i) => [id, i]));

    let states: ColumnState[] = [];

    if (typeof parsed[0] === 'object' && 'colId' in parsed[0]) {
      const savedStates = (parsed as ColumnState[])
        .filter(s => !!s.colId && validIds.has(s.colId))
        .map(enforcePin);

      const present = new Set(savedStates.map(s => s.colId));

      const missing = colDefs
        .map(getColId)
        .filter((id): id is string => !!id && !present.has(id))
        .map(id => enforcePin({colId: id, hide: !critical.has(id)}));

      states = [...savedStates, ...missing];
    } else {
      const seen = new Set<string>();
      const visible = savedIds.filter(id => (seen.has(id) ? false : (seen.add(id), true)))
        .map(id => enforcePin({colId: id, hide: false}));

      const hidden = colDefs
        .map(getColId)
        .filter((id): id is string => !!id && !seen.has(id))
        .map(id => enforcePin({colId: id, hide: !critical.has(id)}));

      states = [...visible, ...hidden];
    }

    states.sort((a, b) => {
      const ai = orderIndex.has(a.colId) ? orderIndex.get(a.colId) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(b.colId) ? orderIndex.get(b.colId) : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return (defOrder.get(a.colId) ?? Number.MAX_SAFE_INTEGER) - (defOrder.get(b.colId) ?? Number.MAX_SAFE_INTEGER);
    });

    const priceIdx = states.findIndex(s => s.colId === 'priceEdits');
    const qtyIdx = states.findIndex(s => s.colId === 'orderQty');

    if (priceIdx !== -1 && qtyIdx !== -1 && priceIdx > qtyIdx) {
      const priceState = states[priceIdx];
      states.splice(priceIdx, 1);
      states.splice(qtyIdx, 0, priceState);
    }
    states[priceIdx].hide = true;
    return states;
  } catch {
    return null;
  }
}

export function loadColumnWidthsIntoDefs(
  columnSizeKey: string,
  colDefs: ColDef[],
): ColDef[] {
  try {
    const raw = localStorage.getItem(columnSizeKey) || '';
    if (!raw) return colDefs;
    const widthMap = JSON.parse(raw) as Record<string, number>;
    if (!widthMap || typeof widthMap !== 'object') return colDefs;
    return colDefs.map(c => {
      const id = c.colId ?? c.field;
      return (id && widthMap[id] ? {...c, width: widthMap[id]} : c);
    });
  } catch {
    return colDefs;
  }
}

export function persistColumnState(
  gridApi: GridApi,
  columnOrderKey: string,
  columnSizeKey: string,
): void {
  if (!gridApi) return;

  // Get all columns with just colId and hide properties to preserve order and visibility
  const state = gridApi.getColumnState().filter(col => !col.hide).map((col) => col.colId);

  if (state && state.length) {
    // Save minimal ColumnState array (just order and visibility)
    localStorage.setItem(columnOrderKey, JSON.stringify(state));
  }
  const widthMap: Record<string, number> = {};
  gridApi.getColumns()?.forEach(col => {
    const id = col.getColId();
    const w = col.getActualWidth();
    if (id && typeof w === 'number') widthMap[id] = w;
  });
  localStorage.setItem(columnSizeKey, JSON.stringify(widthMap));
}

//#endregion

//#region UI HELPERS

const STYLE_ELEMENT_ID = 'ao-line-colours-dynamic-styles';

export function localStorageKeyOrderQuantity(orderId: string): string {
  return `ao-${orderId}-quantity`;
}

export function localStorageKeyOrderPrice(orderId: string): string {
  return `ao-${orderId}-price`;
}

export function localStorageKeyOrderOnHand(orderId: string): string {
  return `ao-${orderId}-on-hand`;
}

//#endregion

//#region EVENT HANDLERS

export function onCellKeyDownCustom(
  cellKeyDownEvent: CellKeyDownEvent,
  gridApi: GridApi,
  isItemOnHoldForPurchaseOrders: (code: string) => boolean,
): void {
  const event = cellKeyDownEvent.event as KeyboardEvent;
  if (event.key === 'Enter' || event.code === 'Enter') {
    event.preventDefault();
    event.stopPropagation();

    const currentColId = cellKeyDownEvent.column.getColId();
    const currentRowIndex = cellKeyDownEvent.rowIndex;

    if (currentColId === 'orderQty' || currentColId === 'priceEdits') {
      gridApi.stopEditing();
      const rowCount = gridApi.getDisplayedRowCount();
      const skipItemsOnHold: boolean = currentColId === 'orderQty';

      let nextRowIndex = currentRowIndex + 1;
      while (nextRowIndex < rowCount) {
        const rowNode = gridApi.getDisplayedRowAtIndex(nextRowIndex);
        const isRowEditable: boolean = !skipItemsOnHold
          || (!!rowNode?.data && !isItemOnHoldForPurchaseOrders(rowNode.data.code as string));
        if (isRowEditable) {
          gridApi.startEditingCell({
            rowIndex: nextRowIndex,
            colKey: currentColId,
          });
          break;
        }

        nextRowIndex++;
      }
    }
  }
}

export function onCellClickedCustom(
  event: CellClickedEvent,
  onToggleOnHold: (code: string, setHold: boolean) => void,
  onNavToFlagged: (storeId: string) => void,
  onItemContextMenu: (event: MouseEvent, code: string) => void,
  storeId: string,
): void {
  const target = event.event.target as HTMLElement;

  const button = target.closest('.on-hold-toggle');
  if (button && button instanceof HTMLElement) {
    const action = button.getAttribute('data-action');
    const code = button.getAttribute('data-code');
    const setHold = button.getAttribute('data-set-hold') === 'true';

    if (action === 'toggle-hold' && code) {
      onToggleOnHold(code, setHold);
      return;
    }
  }

  const mouseEvent = event.event as MouseEvent;
  if (mouseEvent.button === 2 && event.column.getColId() === 'code') {
    const code = event.data.code;
    if (code) {
      onItemContextMenu(mouseEvent, code);
    }
  }
}

//#endregion

//#region EXPORT Functions
export function downloadPreparedData(quantities: Record<string, number>, stockInfoData: Record<string, AOStockInfo>, supplier: ISupplier): void {

  const dataToExport: Record<
    string,
    {
      sku: string;
      qty: number;
      desc: string;
    }
  > = {};
  let counter = 1;

  Object.entries(quantities).forEach(([code, qty]) => {
    const sku = stockInfoData[code]?.suppCode ?? counter.toString();
    dataToExport[code] = {
      sku,
      qty,
      desc: stockInfoData[code]?.desc ?? 'No Description',
    };
    counter++;
  });

  const today = new Date();
  const formatted = today.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  exportDataToExcel(dataToExport, `${formatted}_${supplier?.supplierId}_${supplier?.name}`, supplier?.supplierId ?? 'unknown', [
    'sku',
    'qty',
    'desc',
  ]);
}

//#endregion